import { CommonModule, Location } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssesmentAttemptQuizAttemptData, AssesmentAttemptQuizConversation } from '@edtech-ui/core/models/admin-quiz-enhancement/admin-assessment-attempt-quiz';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { MathLatexComponent } from '@edtech-ui/shared/latex/math-latex/math-latex.component';
import { QuestionBlocksComponent } from '@edtech-ui/shared/question-blocks/question-blocks.component';
import { ToastrService } from 'ngx-toastr';
import { catchError, of, Subscription, tap } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
	selector: 'app-assesment-proto-report',
	standalone: true,
	imports: [CommonModule, MathLatexComponent, QuestionBlocksComponent],
	templateUrl: './assesment-proto-report.component.html',
	styleUrl: './assesment-proto-report.component.scss'
})
export class AssesmentProtoReportComponent {

	@ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
	@ViewChild('lightboxVideo') lightboxVideo?: ElementRef<HTMLVideoElement>;
	@ViewChild('videoContainer') videoContainer?: ElementRef<HTMLDivElement>;

	isPlaying = false;
	currentTime = 0;
	duration = 0;
	volume = 1;
	isMuted = false;
	isDraggingSlider = false;
	wasPlayingBeforeSeek = false;
	selectedVideo: SafeResourceUrl | null = null;
	attemptId!: string;
	subscription = new Subscription();
	reportData!: AssesmentAttemptQuizAttemptData;
	isOpen = true;
	snapshots: any[] = [];
	videoUrl: string | null = null;
	isAnswerOpen = true;
	linkId = '';
	linkName = '';
	previewSnapshotUrl: string | null = null;
	is_proctored: boolean | null = null;
	isFromMockTest = false;

	openSnapshotPreview(url: string) {
		this.previewSnapshotUrl = url;
	}

	closeSnapshotPreview() {
		this.previewSnapshotUrl = null;
	}

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private toastr: ToastrService,
		private quizService: QuizManagementService,
		private sanitizer: DomSanitizer,
		private location: Location
	) { }

	ngOnInit(): void {
		this.isFromMockTest = this.route.snapshot.queryParams['fromMockTest'] === 'true';
		this.route.queryParams.subscribe(params => {
			this.isFromMockTest = params['fromMockTest'] === 'true';
		});
		this.route.paramMap.subscribe(paramMap => {
			const id = paramMap.get('attemptId');
			if (!id) return;
			this.attemptId = id;
			this.getQuizAssessmentAttemptId();
		});
	}

	copyLink() {
		const link = window.location.href;
		navigator.clipboard.writeText(link).then(() => {
			this.toastr.success('Link copied to clipboard');
		}).catch(() => {
			this.toastr.error('Failed to copy link');
		});
	}

	getQuizAssessmentAttemptId() {
		this.subscription.add(
			this.quizService.getQuizAssessmentAttemptId(this.attemptId).pipe(
				tap(response => {
					if (response.Success) {
						this.reportData = response.Data;
						this.videoUrl = response.Data.recording_url || null;
						this.snapshots = (this.reportData.snapshots || []).map((snap: any) => ({
							image: snap.s3_url,
							title: snap.violation_type?.replaceAll('_', ' '),
							time: new Date(snap.timestamp).toLocaleString()
						}));
						this.linkId = this.reportData.link_id || '';
						if (this.linkId) {
							this.getQuizAssesmentById();
						}
					}
				})
			).subscribe()
		);
	}

	getQuizAssesmentById() {
		this.subscription.add(
			this.quizService.getAssesmentById(this.linkId).pipe(
				tap(response => {
					if (response.Success) {
						this.linkName = response.Data.link_name;
						this.is_proctored = response.Data.is_proctored;
					}
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}

	get performance() {
		return this.reportData?.overall_user_performance ?? {
			total_number_of_question: 0,
			total_number_of_correct_answer: 0,
			total_number_of_partial_correct_answer: 0,
			total_number_of_incorrect_answer: 0,
			total_number_of_skipped_answer: 0,
			total_marks: 0,
			gained_points: 0,
			score_percentage: '0%'
		};
	}

	getEvaluation(conv: any) {
		return conv?.evaluation ?? {
			answer_type: conv?.is_skipped
				? 'skipped'
				: conv?.user_answer
					? 'attempted'
					: 'pending',
			correct_answer: null,
			obtained_marks: 0
		};
	}

	get violations() {
		return this.reportData?.violation_type_counts ?? {};
	}

	get totalViolations(): number {
		return this.reportData?.violation_count ?? 0;
	}

	get warningViolationsCount(): number {
		const v = this.violations;
		return (v['FACE_TOO_FAR'] ?? 0) + (v['EYES_CLOSED'] ?? 0) + (v['BACKGROUND_VOICE'] ?? 0)
			+ (v['MULTIPLE_FACES'] ?? 0) + (v['LOOKING_AWAY'] ?? 0) + (v['NO_FACE'] ?? 0);
	}

	get criticalViolationsCount(): number {
		const v = this.violations;
		return (v['TAB_SWITCH'] ?? 0);
	}

	get conversations(): AssesmentAttemptQuizConversation[] {
		return this.reportData?.quiz_conversation ?? [];
	}

	getAnswerStatusClass(type: string): string {
		switch (type) {
			case 'correct': return 'correct';
			case 'incorrect': return 'incorrect';
			case 'partial': return 'partial';
			case 'skipped': return 'skipped';
			case 'attempted': return 'attempted';
			case 'pending': return 'pending';
			default: return '';
		}
	}

	getOptionClass(conv: AssesmentAttemptQuizConversation, optionText: string): string {
		const evaluation = this.getEvaluation(conv);
		const isCorrect = evaluation.correct_answer === optionText;
		const isUserAnswer = conv.user_answer === optionText;

		if (isCorrect) return 'option correct-option';
		if (isUserAnswer && !isCorrect) return 'option wrong-option';
		return 'option';
	}

	isManualDiagram(question: any): boolean {
		return !!question?.diagram_url;
	}


	goBack() {
		if (this.isFromMockTest) {
			this.router.navigate(['/student-dashboard/placement-prep'], { queryParams: { tab: 'history' } });
		} else {
			this.location.back();
		}
	}


	getScoreClass(score: string | number): string {
		const value = typeof score === 'string' ? parseFloat(score) : score;

		if (value <= 30) return 'low-score';
		if (value <= 60) return 'mid-score';
		return 'high-score';
	}

	getCircleProgress(score: number | string): string {
		const value = typeof score === 'string' ? parseFloat(score) : score;

		let color = '#22c55e';

		if (value <= 30) {
			color = '#dc2626';
		} else if (value <= 60) {
			color = '#f59e0b';
		}

		return `conic-gradient(${color} ${value}%, #e5e7eb 0%)`;
	}

	get safeRecordingUrl(): SafeResourceUrl | null {
		if (!this.reportData?.recording_url) return null;
		return this.sanitizer.bypassSecurityTrustResourceUrl(this.reportData.recording_url);
	}

	openVideoLightbox() {
		if (this.safeRecordingUrl) {
			this.selectedVideo = this.safeRecordingUrl;
			this.isPlaying = true;
			this.currentTime = 0;
		}
	}

	closeVideoLightbox() {
		this.selectedVideo = null;
		this.isPlaying = false;
	}

	toggleSection() {
		this.isOpen = !this.isOpen;
	}

	toggleFullscreen() {
		const videoElement = this.videoContainer?.nativeElement;
		if (!videoElement) return;
		if (videoElement.requestFullscreen) {
			videoElement.requestFullscreen();
		} else if ((videoElement as any).webkitRequestFullscreen) {
			(videoElement as any).webkitRequestFullscreen();
		} else if ((videoElement as any).msRequestFullscreen) {
			(videoElement as any).msRequestFullscreen();
		}
	}

	skip(seconds: number) {
		const videoElement = this.lightboxVideo?.nativeElement;
		if (videoElement) {
			videoElement.currentTime += seconds;
		}
	}

	togglePlay() {
		const videoElement = this.lightboxVideo?.nativeElement;
		if (!videoElement) return;

		if (videoElement.paused) {
			videoElement.play();
			this.isPlaying = true;
		} else {
			videoElement.pause();
			this.isPlaying = false;
		}
	}

	toggleMute() {
		this.isMuted = !this.isMuted;
		if (this.lightboxVideo?.nativeElement) {
			this.lightboxVideo.nativeElement.muted = this.isMuted;
			if (!this.isMuted && this.volume === 0) {
				this.volume = 0.5;
				this.lightboxVideo.nativeElement.volume = 0.5;
			}
		}
	}

	onVolumeChange(event: Event) {
		const input = event.target as HTMLInputElement;
		const vol = parseFloat(input.value);
		this.volume = vol;
		if (this.lightboxVideo?.nativeElement) {
			this.lightboxVideo.nativeElement.volume = vol;
			const isNowMuted = vol === 0;
			this.isMuted = isNowMuted;
			this.lightboxVideo.nativeElement.muted = isNowMuted;
		}
	}

	onTimeUpdate(event: Event) {
		if (!this.isDraggingSlider) {
			const video = event.target as HTMLVideoElement;
			this.currentTime = video.currentTime;
			if (video.duration && isFinite(video.duration) && video.duration !== this.duration) {
				this.duration = video.duration;
			}
		}
	}

	onLoadedMetadata(event: Event) {
		const video = event.target as HTMLVideoElement;
		if (isFinite(video.duration)) {
			this.duration = video.duration;
		} else {
			const originalTime = video.currentTime;
			video.currentTime = Number.MAX_SAFE_INTEGER;
			const onSeeked = () => {
				video.removeEventListener('seeked', onSeeked);
				const actualDuration = video.currentTime;
				video.currentTime = originalTime;
				if (isFinite(actualDuration) && actualDuration > 0) {
					this.duration = actualDuration;
				}
			};
			video.addEventListener('seeked', onSeeked);
		}
	}

	onSeekPreview(event: Event) {
		this.isDraggingSlider = true;
		const input = event.target as HTMLInputElement;
		this.currentTime = parseFloat(input.value);

		if (this.lightboxVideo?.nativeElement) {
			this.wasPlayingBeforeSeek = !this.lightboxVideo.nativeElement.paused;
			this.lightboxVideo.nativeElement.pause();
		}
	}

	onSeekEnd(event: Event) {
		this.isDraggingSlider = false;
		const input = event.target as HTMLInputElement;
		const newTime = parseFloat(input.value);
		this.currentTime = newTime;

		if (this.lightboxVideo?.nativeElement) {
			this.lightboxVideo.nativeElement.currentTime = newTime;
			if (this.wasPlayingBeforeSeek) {
				this.lightboxVideo.nativeElement.play();
			}
		}
	}

	seekVideo(event: Event) {
		const input = event.target as HTMLInputElement;
		const videoElement = this.lightboxVideo?.nativeElement;
		if (videoElement) {
			videoElement.currentTime = Number(input.value);
		}
	}


	formatTime(seconds: number): string {
		if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';

		const m = Math.floor(seconds / 60);
		const s = Math.floor(seconds % 60);

		return `${m}:${s < 10 ? '0' : ''}${s}`;
	}


	getRelativeTime(snapshotTime: string): string {
		if (!this.reportData?.start_time) return '';

		const parseUTC = (dateStr: string): Date => {
			if (!dateStr) return new Date();
			let cleanStr = dateStr.trim();
			if (cleanStr.includes(' ') && !cleanStr.includes('T')) {
				cleanStr = cleanStr.replace(' ', 'T');
			}

			// Add Z only if timezone is missing
			if (
				!cleanStr.endsWith('Z') &&
				!/[+-]\d{2}:\d{2}$/.test(cleanStr)
			) {
				cleanStr += 'Z';
			}
			// Add Z only if timezone is missing
			if (
				!cleanStr.endsWith('Z') &&
				!/[+-]\d{2}:\d{2}$/.test(cleanStr)
			) {
				cleanStr += 'Z';
			}

			return new Date(cleanStr);
		};

		const start = parseUTC(this.reportData.start_time).getTime();
		const currentDate = parseUTC(snapshotTime);

		const seconds = Math.max(
			0,
			Math.floor((currentDate.getTime() - start) / 1000)
		);

		const formattedDate = currentDate.toLocaleString('en-IN', {
			timeZone: 'Asia/Kolkata',
			hour12: true
		});

		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		const duration = seconds < 60 ? `${seconds} sec` : secs > 0 ? `${mins} min ${secs} sec` : `${mins} min`;
		return `${formattedDate} (${duration})`;
	}


	toggleAnswerSection() {
		this.isAnswerOpen = !this.isAnswerOpen;
	}


	async downloadRecording(event: Event, url: string | null | undefined) {
		event.stopPropagation();
		event.preventDefault();
		if (!url) return;
		try {
			this.toastr.info('Preparing download, please wait...');
			const response = await fetch(url, { mode: 'cors', referrerPolicy: 'no-referrer' });
			if (!response.ok) throw new Error('Network response was not ok');
			const blob = await response.blob();
			const downloadUrl = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = downloadUrl;
			a.download = `recording_${this.reportData?.student_name?.replace(/\s+/g, '_') || 'student'}.webm`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(downloadUrl);
			this.toastr.clear();
			this.toastr.success('Download completed!');
		} catch (error) {
			this.toastr.clear();
			window.open(url, '_blank');
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}